import React, { useState } from 'react';
import Avatar from '../components/Avatar';
import Icon from '../components/Icon';
import { Alert, Spinner, TopBar } from '../components/ui';
import { api } from '../lib/api';

const BIO_MAX = 200;
const MAX_PHOTOS = 6;

export default function MyProfileScreen({ me, token, onUpdate, onSignOut, onResendVerification, showToast }) {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [removingIndex, setRemovingIndex] = useState(null);
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState(me.bio || '');
  const [savingBio, setSavingBio] = useState(false);

  const photos = me.photos || [];
  const slots = Array.from({ length: MAX_PHOTOS }, (_, i) => photos[i] || null);

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const data = await api.upload('/api/auth/upload-image', form, token);
      onUpdate({ profileImage: data.profileImage });
      showToast('Profile photo updated', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const addPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const data = await api.upload('/api/auth/upload-photo', form, token);
      onUpdate({ photos: data.photos });
      showToast('Photo added', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async (index) => {
    setRemovingIndex(index);
    try {
      const data = await api.del(`/api/auth/delete-photo/${index}`, token);
      onUpdate({ photos: data.photos });
      showToast('Photo removed', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRemovingIndex(null);
    }
  };

  const saveBio = async () => {
    setSavingBio(true);
    try {
      const data = await api.put('/api/auth/update-bio', { bio: bioDraft }, token);
      onUpdate({ bio: data.bio ?? bioDraft });
      setEditingBio(false);
      showToast('Bio updated', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingBio(false);
    }
  };

  return (
    <div className="screen">
      <TopBar title="Your profile" />

      <div className="screen-body">
        <div className="profile-hero">
          <div className="avatar-edit">
            <Avatar user={me} size={104} ring />
            <label className="avatar-edit-btn" title="Change profile photo">
              {uploadingAvatar ? <Spinner label="Uploading photo" /> : <Icon name="camera" size={16} />}
              <input type="file" accept="image/*" onChange={uploadAvatar} disabled={uploadingAvatar} />
              <span className="sr-only">Change profile photo</span>
            </label>
          </div>
          <div className="profile-name">{me.username}</div>
          <div className="profile-email">{me.email}</div>
          <div className="profile-pills">
            {me.isEmailVerified ? (
              <span className="badge badge-verified">
                <Icon name="check" size={11} strokeWidth={2.6} />
                Verified
              </span>
            ) : (
              <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-dim)' }}>
                Unverified
              </span>
            )}
            <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-dim)' }}>
              {photos.length}/{MAX_PHOTOS} photos
            </span>
          </div>
        </div>

        <div className="stack">
          {!me.isEmailVerified && (
            <Alert kind="info">
              Verify your email to make sure you never lose access to your account.{' '}
              <button type="button" className="link-btn" onClick={onResendVerification}>
                Resend link
              </button>
            </Alert>
          )}

          <section className="card">
            <div className="card-head">
              <span className="section-label">About you</span>
              {!editingBio && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setBioDraft(me.bio || '');
                    setEditingBio(true);
                  }}
                >
                  <Icon name="edit" size={15} />
                  Edit
                </button>
              )}
            </div>

            {editingBio ? (
              <div className="field">
                <textarea
                  className="input"
                  rows={4}
                  maxLength={BIO_MAX}
                  value={bioDraft}
                  onChange={(e) => setBioDraft(e.target.value)}
                  placeholder="What should people know about you?"
                  aria-label="Your bio"
                />
                <div className="field-hint">
                  <span>Keep it short and specific — it works better.</span>
                  <span>
                    {bioDraft.length}/{BIO_MAX}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={saveBio} disabled={savingBio}>
                    {savingBio ? <Spinner label="Saving bio" /> : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditingBio(false)}
                    disabled={savingBio}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className={`bio-text${me.bio ? '' : ' bio-empty'}`}>
                {me.bio || 'No bio yet. Add a line or two so people know who they are talking to.'}
              </p>
            )}
          </section>

          <section className="card">
            <div className="card-head">
              <span className="section-label">Your photos</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-faint)' }}>
                {photos.length} of {MAX_PHOTOS}
              </span>
            </div>
            <div className="photo-grid">
              {slots.map((photo, index) =>
                photo ? (
                  <div className="photo-slot" key={`photo-${index}`}>
                    <img src={photo} alt={`Your photo ${index + 1}`} />
                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() => removePhoto(index)}
                      aria-label={`Remove photo ${index + 1}`}
                      disabled={removingIndex === index}
                    >
                      <Icon name="x" size={14} strokeWidth={2.4} />
                    </button>
                    {removingIndex === index && (
                      <div className="photo-slot-busy">
                        <Spinner label="Removing photo" />
                      </div>
                    )}
                  </div>
                ) : index === photos.length ? (
                  <label className="photo-slot photo-slot-empty photo-slot-add" key={`add-${index}`}>
                    {uploadingPhoto ? <Spinner label="Uploading photo" /> : <Icon name="plus" size={24} />}
                    <input type="file" accept="image/*" onChange={addPhoto} disabled={uploadingPhoto} />
                    <span className="sr-only">Add a photo</span>
                  </label>
                ) : (
                  <div className="photo-slot photo-slot-empty" key={`empty-${index}`} aria-hidden="true" />
                )
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <span className="section-label">Account</span>
            </div>
            <button type="button" className="btn btn-danger btn-block" onClick={onSignOut}>
              <Icon name="logout" size={17} />
              Sign out
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
